/**
 * Twitter Embed Component
 *
 * Embeds a tweet using Twitter's widgets.js library.
 * Falls back to a simple link if embed fails.
 */

import { useEffect, useRef, useState } from 'react';
import { ExternalLink, Loader2 } from 'lucide-react';

interface TwitterEmbedProps {
  url: string;
}

// Extend Window interface for Twitter widgets
declare global {
  interface Window {
    twttr?: {
      widgets: {
        load: (element?: HTMLElement) => Promise<void>;
        createTweet: (
          tweetId: string,
          container: HTMLElement,
          options?: { theme?: string; align?: string }
        ) => Promise<HTMLElement | undefined>;
      };
    };
  }
}

/**
 * Extract tweet ID from a Twitter/X URL
 */
function extractTweetId(url: string): string | null {
  const patterns = [/twitter\.com\/\w+\/status\/(\d+)/, /x\.com\/\w+\/status\/(\d+)/];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match?.[1]) {
      return match[1];
    }
  }

  return null;
}

/**
 * Load Twitter widgets.js if not already loaded
 */
function loadTwitterScript(): Promise<void> {
  return new Promise((resolve) => {
    if (window.twttr) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://platform.twitter.com/widgets.js';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => resolve(); // Resolve anyway, we'll handle the fallback
    document.head.appendChild(script);
  });
}

export function TwitterEmbed({ url }: TwitterEmbedProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [status, setStatus] = useState<'loading' | 'loaded' | 'error'>('loading');
  const tweetId = extractTweetId(url);

  useEffect(() => {
    if (!tweetId || !containerRef.current) {
      setStatus('error');
      return;
    }

    let mounted = true;
    const container = containerRef.current;

    const embedTweet = async () => {
      try {
        await loadTwitterScript();

        if (!mounted || !window.twttr) {
          setStatus('error');
          return;
        }

        // Clear any previous content
        container.innerHTML = '';

        const tweet = await window.twttr.widgets.createTweet(tweetId, container, {
          theme: 'light',
          align: 'center',
        });

        if (mounted) {
          setStatus(tweet ? 'loaded' : 'error');
        }
      } catch {
        if (mounted) {
          setStatus('error');
        }
      }
    };

    void embedTweet();

    return () => {
      mounted = false;
    };
  }, [tweetId]);

  // If no tweet ID could be extracted, show fallback immediately
  if (!tweetId) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-2 text-brand-600 hover:underline text-sm"
      >
        <ExternalLink className="h-4 w-4" />
        View on Twitter/X
      </a>
    );
  }

  return (
    <div className="w-full">
      {status === 'loading' && (
        <div className="flex items-center justify-center p-8 text-gray-400">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      )}

      <div ref={containerRef} className={status === 'error' ? 'hidden' : undefined} />

      {status === 'error' && (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 text-brand-600 hover:underline text-sm"
        >
          <ExternalLink className="h-4 w-4" />
          View on Twitter/X
        </a>
      )}
    </div>
  );
}
